import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  DeleteRegionResult,
  Region,
  RegionApiItem,
  UpsertRegionRequest,
} from './region.types';
import { apiUrl } from '../../../auth/api-url';
import { isConflictError, resolveApiErrorMessage } from '../../shared/api-error';

@Injectable({
  providedIn: 'root',
})
export class RegionService {
  private readonly endpoint = apiUrl('region');

  constructor(private readonly http: HttpClient) {}

  getRegions(): Observable<Region[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeRegion(item))),
    );
  }

  createRegion(payload: UpsertRegionRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateRegion(id: number, payload: UpsertRegionRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteRegion(id: number): Observable<DeleteRegionResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteRegionResult => ({ kind: 'success' })),
      catchError((error: unknown) => of<DeleteRegionResult>({
        kind: 'error',
        message: isConflictError(error)
          ? resolveApiErrorMessage('This region still has countries assigned and cannot be removed.', error)
          : 'Unable to delete region at this time. Please try again.',
      })),
    );
  }

  private normalizeRegion(item: RegionApiItem): Region {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: RegionApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): RegionApiItem[] {
    if (Array.isArray(response)) {
      return response as RegionApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as RegionApiItem[];
      }

      if (Array.isArray(items)) {
        return items as RegionApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
